import * as React from "react";
import controls from "../helpers/controls";

import graphics from "../helpers/graphics";
import SceneItem from "./sceneObject";
import CustomObject from "./customObject";
import SimManager from "../managers/simManager";
import MoveMenu from "../modals/moveMenu/moveMenu";
import "./styles.scss";
import { Maybe, StateObject, UserInput, Vector3, Condition } from "../types";
import { InteractableButtons } from "./interactableButtons";
import { Keyframes } from "../helpers/Keyframes";
import { calculateXOffset, calculateYOffset } from "../helpers/responsiveness";
import { IOProps } from "../types/SceneTypes";
import { ObjectState } from "../types/ObjectDefinitionTypes";

//@ts-expect-error Browser Specific Type
const isFirefox = typeof InstallTrigger !== "undefined";

class ControllableObject extends SceneItem {
  private controls = new controls();
  private states: any;
  private currentState: string;
  // eslint-disable-next-line
  private image: Maybe<JSX.Element>;
  private onDropItems: any;
  private userInput: any;
  private moveMenuRef = React.createRef<MoveMenu>();
  // eslint-disable-next-line
  private moveMenuBackground: Maybe<JSX.Element>;
  // eslint-disable-next-line
  private moveMenuImage: Maybe<JSX.Element>;
  private imageSrc: string;
  private hamburgerButtonRef = React.createRef<HTMLElement>();

  constructor(props: any) {
    super(props);
    this.states = props.states;
    this.currentState = props.defaultState;
    this.image = props.image;
    this.onDropItems = props.onDropItems;
    this.userInput = props.defaultUserInput;

    this.moveMenuBackground = graphics
      .getInstance()
      .loadImage(
        "singleButton",
        "",
        [],
        this.state.userScale,
        this.state.sceneScale
      );
    this.moveMenuImage = graphics
      .getInstance()
      .loadImage(
        "moveButton",
        "",
        [],
        this.state.userScale,
        this.state.sceneScale
      );

    this.imageSrc = "";
    if (this.states && props.defaultState) {
      const initialState = this.states.find(
        (s: { name: string }) => s.name === props.defaultState
      );
      if (initialState) {
        this.imageSrc = initialState.image;
        this.image = graphics
          .getInstance()
          .loadImage(
            initialState.image,
            initialState.altText || "",
            [],
            this.state.userScale,
            0.766,
            this.state.objectScale
          );
      }
    }

    this.state = {
      position: this.props.position,
      userScale: this.props.userScale,
      sceneScale: this.props.sceneScale,
      objectScale: this.props.objectScale,
      image: this.image,
      text: this.props.text,
      currentState: props.defaultState,
      userInput: props.defaultUserInput,
      showLabel: <></>,
      hover: "",
      isHovered: false,
    };
  }

  componentDidMount(): void {
    SimManager.getInstance().registerControllableObject(this);
    // eslint-disable-next-line
    document.addEventListener("mousedown", this.handleClickOutside);

    //Setting move menu options from props
    if (this.props.moveMenu) {
      const moveMenuOptions = Object.keys(this.props.moveMenu);
      this.moveMenuRef.current?.setMoveMenuOptions(moveMenuOptions);
    }
  }

  handleClickOutside = (event: any) => {
    if (event && event.target && event.target.parentElement) {
      if (event.target.parentElement.id != this.props.id)
        this.setState({ showLabel: <></> });
    }
  };

  getId(): string {
    return this.props.id;
  }

  getName(): string {
    return this.props.name;
  }

  setItemState(postState: string) {
    const changeState = this.states.filter(
      (x: { name: string }) => x.name === postState
    );

    if (changeState.length > 0) {
      const image = graphics
        .getInstance()
        .loadImage(
          changeState[0].image,
          changeState[0].altText || "",
          [],
          this.state.userScale,
          this.state.sceneScale,
          this.state.objectScale
        );
      this.imageSrc = changeState[0].image;
      this.image = image;
      this.setState(
        { currentState: postState },
        SimManager.getInstance().handleUpdateItems
      );
      this.currentState = postState;
    }
  }

  onItemDropped = (item: ControllableObject | CustomObject) => {
    this.onDropItems.forEach((element: any) => {
      if (element.item == "Self") {
        if (
          element.droppedItemName.includes(item.getId()) &&
          (this.state.currentState == element.initialState ||
            (!element.initialState &&
              (!element.initialStates ||
                element.initialStates?.includes(this.state.currentState))))
        ) {
          let conditionsChecked = false;
          if (!element.conditions) {
            this.setItemState(
              (
                element.transitions?.find(
                  (x: { initialState: string }) =>
                    x.initialState === this.state.currentState
                ) || element
              ).postState
            );
            SimManager.dispatchAction(element.actionName, {
              id: this.getId(),
              droppedId: item.getId(),
            });
            element.message &&
              SimManager.getInstance().addToast(
                element.message.title,
                element.message.message,
                element.message.type
              );
          } else {
            element.conditions.forEach((condition: Condition) => {
              const conditionItem =
                condition.conditionItem === "Dropped"
                  ? item.getId()
                  : this.getId();
              if (
                SimManager.getInstance().checkCondition({
                  ...condition,
                  conditionItem,
                })
              ) {
                conditionsChecked = true;
              }
            });
          }

          if (conditionsChecked) {
            this.setItemState(
              (
                element.transitions?.find(
                  (x: { initialState: string }) =>
                    x.initialState === this.state.currentState
                ) || element
              ).postState
            );
            SimManager.dispatchAction(element.actionName, {
              id: this.getId(),
              droppedId: item.getId(),
            });
            element.message &&
              SimManager.getInstance().addToast(
                element.message.title,
                element.message.message,
                element.message.type
              );
          }
        }
      } else if (element.item == "Dropped") {
        if (element.droppedItemName.includes(item.getId())) {
          if (
            item.getCurrentState() === element.initialState ||
            (!element.initialState &&
              (!element.initialStates ||
                element.initialStates?.includes(item.getCurrentState())))
          ) {
            let conditionsChecked = false;
            if (!element.conditions) {
              SimManager.updateObjectState(
                item.getId(),
                (
                  element.transitions?.find(
                    (x: { initialState: string }) =>
                      x.initialState === item.getCurrentState()
                  ) || element
                ).postState,
                undefined,
                element.resetPosition
              );
              SimManager.dispatchAction(element.actionName, {
                id: this.getId(),
                droppedId: item.getId(),
              });
              element.message &&
                SimManager.getInstance().addToast(
                  element.message.title,
                  element.message.message,
                  element.message.type
                );
            } else {
              element.conditions.forEach((condition: Condition) => {
                const conditionItem =
                  condition.conditionItem === "Dropped"
                    ? item.getId()
                    : this.getId();
                if (
                  SimManager.getInstance().checkCondition({
                    ...condition,
                    conditionItem,
                  })
                )
                  conditionsChecked = true;
              });
            }

            if (conditionsChecked) {
              SimManager.updateObjectState(
                item.getId(),
                (
                  element.transitions?.find(
                    (x: { initialState: string }) =>
                      x.initialState === item.getCurrentState()
                  ) || element
                ).postState,
                undefined,
                element.resetPosition
              );
              SimManager.dispatchAction(element.actionName, {
                id: this.getId(),
                droppedId: item.getId(),
              });
              element.message &&
                SimManager.getInstance().addToast(
                  element.message.title,
                  element.message.message,
                  element.message.type
                );
            }
          }
        }
      }
    });
  };

  onDropped(objectState: StateObject | void) {
    //If there are not state changes occuring
    if (!objectState) {
      return;
    }

    if (objectState.initialState && objectState.postState)
      if (this.currentState == objectState.initialState)
        this.setItemState(objectState.postState);

    if (objectState.position)
      if (
        objectState.position.x != 0 ||
        objectState.position.y != 0 ||
        objectState.position.z != 0
      )
        this.setItemPosition(objectState.position);

    if (objectState.rotation)
      if (
        objectState.rotation.x != 0 ||
        objectState.rotation.y != 0 ||
        objectState.rotation.z != 0
      )
        this.setItemRotation(objectState.rotation);
  }

  setItemPosition(position?: Vector3, reset = false) {
    this.setState({
      position: reset ? this.props.position : position || this.state.position,
    });
  }

  getItemPosition(): Vector3 {
    return this.state.position;
  }

  // eslint-disable-next-line
  setItemRotation(rotation: Vector3) {}

  getCurrentState(): string {
    return this.currentState;
  }

  updateUserInput(newValuePair: UserInput) {
    this.userInput = { ...this.userInput, ...newValuePair };
    SimManager.getInstance().handleUpdateItems();
  }

  getCurrentInput() {
    return this.userInput;
  }

  setScale(userScale: number, sceneScale: number) {
    this.setState({ userScale: userScale, sceneScale: sceneScale });
  }

  moveMenuPressed = () => {
    this.moveMenuRef.current?.openMoveMenu();
  };

  handleMoveMenuKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.moveMenuPressed();
    }
  };

  setHamburgerButtonRef = (ref: React.RefObject<HTMLElement>) => {
    this.hamburgerButtonRef = ref;
    this.moveMenuRef.current?.setHamburgerButtonRef(ref);
  };

  moveMenuButton = () => {
    const ButtonCSSStyle: React.CSSProperties = {
      //@ts-expect-error: Setting css scene-scale
      "--scene-scale": `${(this.state.sceneScale * 100).toFixed(0)}%`,
      left: 0,
      top: 0,
      transform:
        `translateX(` +
        this.translationX(this.state.position.x) +
        "px) translateY(" +
        this.translationY(this.state.position.y) +
        `px)`,
      position: "absolute",
    };

    return (
      <div style={ButtonCSSStyle}>
        <div
          style={{
            width: "150px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-evenly",
            zIndex: 1,
          }}
        >
          <div
            style={{
              position: "absolute",
              height: "100%",
              zIndex: 0,
              paddingLeft: "30px",
            }}
          >
            {this.moveMenuBackground}
          </div>

          <div
            role="button"
            tabIndex={0}
            aria-label={this.getAriaLabel()}
            style={{
              position: "relative",
              top: 0,
              left: 0,
              paddingLeft: "30px",
            }}
            id={this.props.id}
            onKeyDown={this.handleMoveMenuKeyDown}
            onClick={this.moveMenuPressed}
          >
            {this.moveMenuImage}
          </div>
        </div>
      </div>
    );
  };

  removeMoveMenuButton() {
    this.setState({ showLabel: <></> });
  }

  translationX = (x: number) => {
    return Math.floor(
      this.state.sceneScale > 1
        ? x * this.state.sceneScale
        : x * this.state.sceneScale
    );
  };
  translationY = (y: number) => {
    return Math.floor(y * this.state.sceneScale);
  };

  getButtons = () => {
    const currentState = this.states.find(
      (state: { name: string }) => state.name === this.state.currentState
    );
    if (currentState && currentState.buttons) {
      return [
        ...(this.props.buttons ? this.props.buttons : []),
        ...currentState.buttons,
      ];
    }
    return this.props.buttons;
  };

  getLabel = () => {
    return (
      this.states.find(
        (state: { name: string }) => state.name === this.state.currentState
      )?.label || this.props.label
    );
  };

  getAriaLabel = () => {
    const label = this.getLabel();
    if (!label) {
      return this.props.id;
    }
    if (typeof label === "string") {
      return label;
    }
    if (typeof label === "object" && "text" in label) {
      return label.text;
    }
    if (
      typeof label === "object" &&
      "topLine" in label &&
      "bottomLine" in label
    ) {
      return `${label.topLine.text} ${label.bottomLine.text}`;
    }
    return this.props.id;
  };

  getAnimation = () => {
    const animation = this.states.find(
      (state: { name: string }) => state.name === this.state.currentState
    )?.animation;
    if (!animation) return null;
    return {
      css: {
        animationName: animation.name,
        animationDuration: `${animation.duration}s`,
        animationDelay: animation.delay || 0,
        animationIterationCount: animation.iterationCount || 1,
        animationDirection: animation.direction || "normal",
        animationTimingFunction: animation.timingFunction || "linear",
      },
      ...animation,
    };
  };

  updateAltText = (text: string) => {
    const image = graphics
      .getInstance()
      .loadImage(
        this.imageSrc,
        text,
        [],
        this.state.userScale,
        0.766,
        this.state.objectScale
      );

    if (image) this.setState({ image: image });
  };

  render() {
    const animation = this.getAnimation();
    const pos: React.CSSProperties = {
      transform:
        `translateX(` +
        calculateXOffset(this.state.position.x) +
        ") translateY(" +
        calculateYOffset(this.state.position.y) +
        `)`,
      position: "absolute",
      zIndex: this.props.position.z,
      ...(animation ? animation.css : {}),
    };

    // eslint-disable-next-line
    const onDragStart = (e: React.DragEvent<HTMLDivElement>) => {
      this.controls.dragStart(e);
    };

    // eslint-disable-next-line
    const dragging = (e: React.DragEvent<HTMLDivElement>) => {
      this.controls.dragging(e, this);
    };

    // eslint-disable-next-line
    const onDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
      this.controls.dragEnd(e, handleDrop, this.props.id);
    };

    // eslint-disable-next-line
    const handleDrop = (div: HTMLDivElement | null) => {
      //Send message to sim manager to update state of the item that has been dropped on
      if (div) SimManager.getInstance().handleItemDrop(this.props.id, div.id);
      else SimManager.getInstance().handleItemDrop(this.props.id, "");

      //Updating the controllable item's own state according to the json data
    };

    const handleMouseEnter = () => {
      this.setState({
        isHovered: true,
      });
    };

    const handleMouseLeave = () => {
      this.setState({
        isHovered: false,
      });
    };

    const handleFocus = () => {
      this.setState({
        isHovered: true,
      });
    };

    const handleBlur = () => {
      this.setState({
        isHovered: false,
      });
    };

    const onClickEvent = () => {
      this.setState({ showLabel: this.moveMenuButton() });
    };

    const handleClickKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onClickEvent();
      }
    };

    return (
      <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        {animation && <Keyframes name={animation.name} {...animation} />}
        <div
          role="button"
          tabIndex={0}
          aria-label={this.getAriaLabel()}
          id={this.props.id}
          style={pos}
          className="graphics"
          draggable="true"
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onClick={onClickEvent}
          data-object-state={this.state.currentState}
          onKeyDown={handleClickKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          {...(isFirefox ? { onDragOver: dragging } : { onDrag: dragging })}
          {...(!this.image && { tabIndex: -1 })}
        >
          {this.image && (
            <InteractableButtons
              buttons={this.getButtons()}
              label={this.getLabel()}
              visible={this.state.isHovered}
              scope={this.props.id}
              moveMenu={this.props.moveMenu}
              onOpenMoveMenu={this.moveMenuPressed}
              onSetHamburgerButtonRef={this.setHamburgerButtonRef}
            />
          )}
          {this.image}
        </div>
        <MoveMenu
          item={this}
          ref={this.moveMenuRef}
          hamburgerButtonRef={this.hamburgerButtonRef}
        />
        {/* {this.state.showLabel} */}
      </div>
    );
  }
}

export default ControllableObject;
